import { ActionIcon, Text } from "@mantine/core";
import { IconX } from "@tabler/icons-react";
import { formatNumberAsCurrencyString } from "common/utils";
import {
  ProductQuickSearchInput,
  ProductQuickSearchInputHandle,
} from "pages/admin/components/ProductQuickSearchInput";
import React, { useRef, useState } from "react";
import { SupplierOrderEntryRowProps } from "types/interface.types";
/**
 * Note: This component uses intrinsic controls as oppose to mantine <NumberInput> and <Select> becuase of how expensive
 * it is with rendering.
 * especially when we have large sets of entries to input...
 *
 */
const EntryRowComponent: React.FC<SupplierOrderEntryRowProps> = ({
  entry,
  index,
  isLastRow,
  onUpdate,
  onRemove,
  onNewEntry,
  orderReceived,
}) => {
  const [localProductId, setLocalProductId] = useState(entry.productId);
  const [localQuan, setLocalQuan] = useState(entry.quantity);
  const [localActual, setLocalActual] = useState(entry.actual);
  const [localComms, setLocalComms] = useState(entry.commission);
  const [localPr1, setLocalPr1] = useState(entry.pr1Percent);
  const [localCost, setLocalCost] = useState(entry.newCostPrice);
  const [localSellPrice1, setLocalSellPrice1] = useState(entry.newSellPrice1);
  const [localSellPrice2, setLocalSellPrice2] = useState(entry.newSellPrice2);
  const [localBestBeforeDate, setBestBeforeDate] = useState<string | null>(
    entry.bestBeforeDate,
  );
  const [localBarcode] = useState(entry.barcode);
  const productInputRef = useRef<ProductQuickSearchInputHandle | null>(null);

  return (
    <div
      data-row-index={index}
      data-row-id={index || entry.id}
      style={{
        display: "grid",
        gridTemplateColumns: orderReceived
          ? "0.5fr 1.5fr 0.5fr 0.5fr 0.5fr 0.5fr 0.5fr 0.5fr 0.5fr 0.5fr 0.5fr 0.5fr 0.5fr 0.5fr 0.5fr"
          : "0.5fr 3.5fr 0.5fr 0.5fr 0.5fr 0.5fr",
        gap: "8px",
        marginBottom: "2px",
        width: "100%",
        alignItems: "center", // 👈 ensures inputs and text align vertically
      }}
    >
      {/* Note: we use local variables as we need to implement onChange.  We don't want to bind directly to the parent as it will be very expensive with re-rendering */}
      <ProductQuickSearchInput
        ref={productInputRef}
        value={localProductId}
        onChange={(newId) => {
          setLocalProductId(newId);
          if (newId !== entry.productId) {
            onUpdate({
              ...entry, // only for fields that never change locally
              productId: newId,
              quantity: localQuan ?? 0,
              actual: localActual ?? 0,
              commission: localComms,
              pr1Percent: localPr1,
              newCostPrice: localCost ?? 0,
              newSellPrice1: localSellPrice1 ?? 0,
              newSellPrice2: localSellPrice2 ?? 0,
              bestBeforeDate: localBestBeforeDate ?? "",
            });

            setTimeout(() => {
              requestAnimationFrame(() => {
                productInputRef.current?.blur();
              });
            }, 0);
          }
        }}
      />
      <input
        type="text"
        readOnly
        tabIndex={-1}
        value={entry.productDescription}
        className="mantine-input"
        style={{
          width: "100%",
          height: 32,
          minWidth: 0,
          paddingRight: 28, // make room for icon
        }}
      />
      <input
        type="number"
        value={localQuan}
        onChange={(e) => {
          const val = Number(e.target.value);
          if (!isNaN(val)) setLocalQuan(val); // local only
        }}
        onBlur={(e) => {
          if (localQuan !== entry.quantity) {
            onUpdate({
              ...entry, // only for fields that never change locally
              productId: localProductId,
              quantity: localQuan ?? 0,
              actual: localActual ?? 0,
              commission: localComms,
              pr1Percent: localPr1,
              newCostPrice: localCost ?? 0,
              newSellPrice1: localSellPrice1 ?? 0,
              newSellPrice2: localSellPrice2 ?? 0,
              bestBeforeDate: localBestBeforeDate ?? "",
            });
          }
        }}
        className="mantine-input"
        style={{ width: "100%", height: 32, minWidth: 0 }}
      />
      <input
        type="number"
        value={localActual}
        onChange={(e) => {
          const val = Number(e.target.value);
          if (!isNaN(val)) setLocalActual(val); // local only
        }}
        onBlur={(e) => {
          if (localActual !== entry.actual) {
            onUpdate({
              ...entry, // only for fields that never change locally
              productId: localProductId,
              quantity: localQuan ?? 0,
              actual: localActual ?? 0,
              commission: localComms,
              pr1Percent: localPr1,
              newCostPrice: localCost ?? 0,
              newSellPrice1: localSellPrice1 ?? 0,
              newSellPrice2: localSellPrice2 ?? 0,
              bestBeforeDate: localBestBeforeDate ?? "",
            });
          }
        }}
        className={`mantine-input ${localActual > localQuan && " over-supply"}`}
        style={{ width: "100%", height: 32, minWidth: 0 }}
      />
      {orderReceived && (
        <>
          <input
            type="number"
            value={localComms}
            onChange={(e) => {
              const val = Number(e.target.value);
              if (!isNaN(val)) setLocalComms(val); // local only
            }}
            onBlur={(e) => {
              if (localComms !== entry.commission) {
                onUpdate({
                  ...entry, // only for fields that never change locally
                  productId: localProductId,
                  quantity: localQuan ?? 0,
                  actual: localActual ?? 0,
                  commission: localComms,
                  pr1Percent: localPr1,
                  newCostPrice: localCost ?? 0,
                  newSellPrice1: localSellPrice1 ?? 0,
                  newSellPrice2: localSellPrice2 ?? 0,
                  bestBeforeDate: localBestBeforeDate ?? "",
                });
              }
            }}
            className="mantine-input"
            style={{ width: "100%", height: 32, minWidth: 0 }}
          />
          <input
            type="number"
            value={localPr1}
            onChange={(e) => {
              const val = Number(e.target.value);
              if (!isNaN(val)) setLocalPr1(val); // local only
            }}
            onBlur={(e) => {
              if (localPr1 !== entry.pr1Percent) {
                onUpdate({
                  ...entry, // only for fields that never change locally
                  productId: localProductId,
                  quantity: localQuan ?? 0,
                  actual: localActual ?? 0,
                  commission: localComms,
                  pr1Percent: localPr1,
                  newCostPrice: localCost ?? 0,
                  newSellPrice1: localSellPrice1 ?? 0,
                  newSellPrice2: localSellPrice2 ?? 0,
                  bestBeforeDate: localBestBeforeDate ?? "",
                });
              }
            }}
            className="mantine-input"
            style={{ width: "100%", height: 32, minWidth: 0 }}
          />
          <Text size="sm">
            {formatNumberAsCurrencyString(entry.oldCostPrice)}
          </Text>
          <input
            type="number"
            value={localCost}
            onChange={(e) => {
              const val = Number(e.target.value);
              if (!isNaN(val)) setLocalCost(val); // local only
            }}
            onBlur={(e) => {
              if (localCost !== entry.newCostPrice) {
                onUpdate({
                  ...entry, // only for fields that never change locally
                  productId: localProductId,
                  quantity: localQuan ?? 0,
                  actual: localActual ?? 0,
                  commission: localComms,
                  pr1Percent: localPr1,
                  newCostPrice: localCost ?? 0,
                  newSellPrice1: localSellPrice1 ?? 0,
                  newSellPrice2: localSellPrice2 ?? 0,
                  bestBeforeDate: localBestBeforeDate ?? "",
                });
              }
            }}
            className="mantine-input"
            style={{ width: "100%", height: 32, minWidth: 0 }}
          />
          <Text size="sm">
            {formatNumberAsCurrencyString(entry.oldSellPrice1)}
          </Text>
          <input
            type="number"
            value={localSellPrice1}
            onChange={(e) => {
              const val = Number(e.target.value);
              if (!isNaN(val)) setLocalSellPrice1(val); // local only
            }}
            onBlur={(e) => {
              if (localSellPrice1 !== entry.newSellPrice1) {
                onUpdate({
                  ...entry, // only for fields that never change locally
                  productId: localProductId,
                  quantity: localQuan ?? 0,
                  actual: localActual ?? 0,
                  commission: localComms,
                  pr1Percent: localPr1,
                  newCostPrice: localCost ?? 0,
                  newSellPrice1: localSellPrice1 ?? 0,
                  newSellPrice2: localSellPrice2 ?? 0,
                  bestBeforeDate: localBestBeforeDate ?? "",
                });
              }
            }}
            className="mantine-input"
            style={{ width: "100%", height: 32, minWidth: 0 }}
          />
          {/* price change */}
          <Text size="sm">
            {formatNumberAsCurrencyString(entry.priceChange)}
          </Text>
          {/* sp2*/}
          <Text size="sm">
            {formatNumberAsCurrencyString(entry.oldSellPrice2)}
          </Text>
          <input
            type="number"
            value={localSellPrice2}
            onChange={(e) => {
              const val = Number(e.target.value);
              if (!isNaN(val)) setLocalSellPrice2(val); // local only
            }}
            onBlur={(e) => {
              if (localSellPrice2 !== entry.newSellPrice2) {
                onUpdate({
                  ...entry, // only for fields that never change locally
                  productId: localProductId,
                  quantity: localQuan ?? 0,
                  actual: localActual ?? 0,
                  commission: localComms,
                  pr1Percent: localPr1,
                  newCostPrice: localCost ?? 0,
                  newSellPrice1: localSellPrice1 ?? 0,
                  newSellPrice2: localSellPrice2 ?? 0,
                  bestBeforeDate: localBestBeforeDate ?? "",
                });
              }
            }}
            className="mantine-input"
            style={{ width: "100%", height: 32, minWidth: 0 }}
          />
          <input
            type="text"
            value={localBestBeforeDate ?? ""}
            maxLength={10}
            onChange={(e) => {
              const val = e.target.value;
              setBestBeforeDate(val); // local only
            }}
            onBlur={(e) => {
              if (localBestBeforeDate !== entry.bestBeforeDate) {
                onUpdate({
                  ...entry, // only for fields that never change locally
                  productId: localProductId,
                  quantity: localQuan ?? 0,
                  actual: localActual ?? 0,
                  commission: localComms,
                  pr1Percent: localPr1,
                  newCostPrice: localCost ?? 0,
                  newSellPrice1: localSellPrice1 ?? 0,
                  newSellPrice2: localSellPrice2 ?? 0,
                  bestBeforeDate: localBestBeforeDate ?? "",
                });
              }
            }}
            className="mantine-input"
            style={{ width: "100%", height: 32, minWidth: 0 }}
          />
        </>
      )}

      {/* hidden fields */}
      <input type="hidden" value={entry.oldRetail} />
      <input type="hidden" value={entry.oldSellPrice1} />
      <input type="hidden" value={entry.oldSellPrice2} />
      <input type="hidden" value={entry.oldCostPrice} />

      {!orderReceived && <Text size="sm">{localBarcode}</Text>}

      <ActionIcon
        onClick={onRemove}
        onKeyDown={(e) => {
          if (e.key === "Tab" && !e.shiftKey) {
            // only trigger new row if this is the last row
            if (isLastRow) {
              e.preventDefault(); // stop default tabbing
              onNewEntry?.();
            }
          }
        }}
      >
        <IconX size={18} />
      </ActionIcon>
    </div>
  );
};

// Only re-render if returned or productCode changes
const areEqual = (
  prev: SupplierOrderEntryRowProps,
  next: SupplierOrderEntryRowProps,
) => {
  return (
    prev.entry.quantity === next.entry.quantity &&
    prev.entry.actual === next.entry.actual &&
    prev.entry.productId === next.entry.productId
  );
};

export const OrderEntryRow = React.memo(EntryRowComponent, areEqual);
